import type { Component, DefineComponent } from 'vue';
import type { TCashierTab } from '~/store/cashier';
import {
  LazyModalCashier,
  LazyModalGeneric,
  LazyModalInsufficientFunds,
  LazyModalSignIn,
  LazyModalSignUp,
} from '#components';

type GetProps<T extends Component> = T extends DefineComponent<infer P, any, any, any, any, any, any, any> ? P : object;

export const useModalStore = defineStore('modals', () => {
  const overlay = useOverlay();
  const gtag = useGtag();
  const cache = new Map<string, ReturnType<typeof overlay.create>>();
  const openModals = ref<string[]>([]);

  async function open<T extends Component>(name: string, modalRef: T, data?: GetProps<T>) {
    if (!cache.has(name)) {
      const modal = overlay.create(modalRef);
      cache.set(name, modal);
    }

    const overlayInstance = cache.get(name);
    if (!overlayInstance) return;

    const instance = overlayInstance.open(data);

    if (instance.isOpen) {
      openModals.value.push(name);
    }

    const result = (await instance.result) as ReturnType<typeof overlay.create<T>> & { openNext?: IOpenNextData };

    if (result?.openNext) {
      const { name, modalRef, data } = result.openNext as IOpenNextData;
      await open(name, modalRef, data);
    }

    return result;
  }

  function close(name: string) {
    if (!cache.has(name)) return;
    cache.get(name)?.close();
    openModals.value = openModals.value.filter((modal) => modal !== name);
  }

  async function showInsufficientFundsModal(
    options: { game?: IGameData; text?: string } = {
      text: 'Please visit cashier in order to deposit to your balance.',
    },
  ) {
    if (options?.game) {
      return open('insufficient-funds', LazyModalInsufficientFunds, { game: options.game });
    }

    return open('insufficient-funds', LazyModalGeneric, {
      title: 'Insufficient balance',
      text: options.text,
      actions: [
        {
          label: 'Cancel',
          color: 'neutral',
          class: 'bg-light-gray text-dark hover:bg-dark-gray/20',
        },
        {
          label: 'Deposit Now',
          color: 'primary',
          onClick: () => showCashierModal(),
        },
      ],
    });
  }

  function showSignUpModal() {
    return open('sign-up', LazyModalSignUp);
  }

  function showSignInModal() {
    return open('sign-in', LazyModalSignIn);
  }

  async function showCashierModal(options?: { tabName: TCashierTab }) {
    gtag.reportGtagConversion();
    return open('cashier', LazyModalCashier, options);
  }

  async function showNetworkErrorModal(title: string, text: string) {
    return open('network-error', LazyModalGeneric, {
      title,
      text,
      actions: [{ label: 'OK', color: 'primary' }],
    });
  }

  function hasAnyOpen() {
    return openModals.value.length > 0;
  }

  return {
    open,
    close,
    openModals,
    showInsufficientFundsModal,
    showSignUpModal,
    showSignInModal,
    showCashierModal,
    showNetworkErrorModal,
    hasAnyOpen,
  };
});
